import React, { useState, useEffect } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { useQuery } from "@apollo/client";
import { GET_MATCH_VIDEOS } from "./VideoQueries";
import { getMatchVideosREST, deleteVideoFromMatchREST } from "./RESTUpload";
import { Box, Grid, Typography as MuiTypography, CircularProgress } from "@material-ui/core";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import Typography from "../typography";
import VideoCard from "./VideoCard";
import VideoPlayer from "./VideoPlayer";
import VideoUploader from "./VideoUploader";

interface Video {
  id: string;
  title?: string;
  description?: string;
  youtubeUrl?: string;
  youtubeVideoId?: string;
  fileUrl?: string;
  thumbnail?: string;
  duration?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

interface VideoListProps {
  matchId: string;
  maxVideos?: number;
  readonly?: boolean;
  videos?: Video[];
}

// Styled Components
const Container = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "100%",
  marginTop: theme.spacing(3),
}));

const Header = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const HeaderTitle = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const Counter = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  padding: theme.spacing(0.5, 2),
  borderRadius: "20px",
  fontWeight: 700,
  fontSize: "0.9rem",
}));

const VideosGrid = styled(Grid)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
}));

const EmptyState = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6, 2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "12px",
  marginTop: theme.spacing(2),
}));

const EmptyIcon = styled(SentimentDissatisfiedIcon)(({ theme }: IStyledArguments) => ({
  fontSize: "4rem",
  color: theme.palette.grey[400],
  marginBottom: theme.spacing(2),
}));

const LoadingContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
}));

const Divider = styled(Box)(({ theme }: IStyledArguments) => ({
  height: "1px",
  background: theme.palette.divider,
  margin: theme.spacing(3, 0),
}));

// Processing Video Card Component
const ProcessingCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.default,
  borderRadius: "12px",
  overflow: "hidden",
  cursor: "not-allowed",
  opacity: 0.7,
  border: `2px dashed ${theme.palette.extra.warning}`,
  position: "relative",
}));

const ProcessingThumbnail = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "100%",
  height: "180px",
  background: theme.palette.extra.warningSoft,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const ProcessingInfo = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(1.5, 2),
}));

const ProcessingVideoCard: React.FC<{ video: Video }> = ({ video }) => {
  return (
    <ProcessingCard>
      <ProcessingThumbnail>
        <HourglassEmptyIcon style={{ fontSize: "3rem", color: "#d97706" }} />
        <CircularProgress size={20} style={{ color: "#d97706" }} />
        <MuiTypography 
          variant="body2" 
          style={{ 
            color: "#d97706", 
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Processing...
        </MuiTypography>
      </ProcessingThumbnail>
      <ProcessingInfo>
        <MuiTypography 
          variant="body2" 
          style={{ 
            fontWeight: 600,
            marginBottom: "4px",
            color: "#666",
          }}
        >
          {video.title}
        </MuiTypography>
        <MuiTypography 
          variant="caption" 
          style={{ color: "#999" }}
        >
          Uploading to YouTube...
        </MuiTypography>
      </ProcessingInfo>
    </ProcessingCard>
  );
};

/**
 * VideoList Component
 * Displays a grid of match videos with upload functionality
 */
const VideoList: React.FC<VideoListProps> = ({ matchId, maxVideos = 20, readonly = false, videos: externalVideos }) => {
  const [videos, setVideos] = useState<Video[]>(externalVideos || []);
  const [processingVideos, setProcessingVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [restVideos, setRestVideos] = useState<any[]>([]);
  const [useRest, setUseRest] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_MATCH_VIDEOS, {
    variables: { id: matchId },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all", // Continue even if there are GraphQL errors
    skip: readonly && !!externalVideos, // Skip query if readonly and videos are provided
  });

  // Function to refresh videos using REST API
  const refreshVideosREST = async () => {
    try {
      console.log("🔄 Refreshing videos using REST API");
      const restData = await getMatchVideosREST(matchId);
      if (restData?.data?.videos) {
        setRestVideos(restData.data.videos);
        setUseRest(true);
        console.log("✅ REST videos loaded:", restData.data.videos);
      }
    } catch (error) {
      console.error("❌ Error refreshing videos with REST:", error);
    }
  };

  // Update videos when external videos prop changes
  useEffect(() => {
    if (externalVideos && readonly) {
      setVideos(externalVideos);
    }
  }, [externalVideos, readonly]);

  // Update videos when GraphQL data changes
  useEffect(() => {
    if (data?.match?.videos && !useRest && !readonly) {
      // Sort videos by upload date (most recent first)
      const sortedVideos = [...data.match.videos].sort((a, b) => {
        return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
      });
      setVideos(sortedVideos);
    }
  }, [data, useRest, readonly]);

  // Handle GraphQL errors by falling back to REST
  useEffect(() => {
    if (error) {
      console.warn("⚠️ GraphQL error detected, falling back to REST:", error);
      refreshVideosREST();
    }
  }, [error, matchId]);

  // Convert REST videos to GraphQL format for compatibility
  useEffect(() => {
    if (useRest && restVideos.length > 0) {
      const convertedVideos = restVideos.map(video => ({
        id: video.id,
        title: video.title || 'Video',
        description: video.description || '',
        youtubeUrl: undefined, // Not a YouTube video
        youtubeVideoId: undefined, // Not a YouTube video
        fileUrl: video.url || video.fileUrl, // URL directa del archivo
        thumbnail: video.thumbnail || '', 
        duration: video.duration || '',
        uploadedAt: video.uploadedAt,
        uploadedBy: video.uploadedBy || ''
      }));
      setVideos(convertedVideos);
    }
  }, [useRest, restVideos]);

  /**
   * Handles video card click to open player
   */
  const handleVideoClick = (video: Video) => {
    setSelectedVideo(video);
    setIsPlayerOpen(true);
  };

  /**
   * Handles video deletion
   */
  const handleVideoDelete = async (videoId: string) => {
    if (!confirm("Are you sure you want to delete this video? This action cannot be undone.")) {
      return;
    }

    try {
      console.log(`🗑️ Starting delete for video ${videoId} in match ${matchId}`);
      const result = await deleteVideoFromMatchREST(matchId, videoId);
      console.log(`✅ Delete result:`, result);
      
      // Refresh videos after deletion
      await refreshVideosREST();
      console.log("✅ Video deleted and list refreshed successfully");
    } catch (error: any) {
      console.error("❌ Error deleting video:", error);
      alert(`Error deleting video: ${error.message || 'Please try again.'}`);
    }
  };

  /**
   * Handles opening video player
   */
  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    // Delay clearing selected video for smooth animation
    setTimeout(() => setSelectedVideo(null), 300);
  };

  /**
   * Adds a processing video placeholder
   */
  const addProcessingVideo = (fileName: string) => {
    const processingVideo: Video = {
      id: `processing-${Date.now()}`, // Temporary ID
      title: fileName.replace(/\.[^/.]+$/, ""), // Remove extension
      description: "Processing...",
      youtubeUrl: undefined,
      youtubeVideoId: undefined,
      fileUrl: undefined,
      thumbnail: undefined,
      duration: undefined,
      uploadedAt: new Date().toISOString(),
      uploadedBy: "You",
    };
    
    setProcessingVideos(prev => [...prev, processingVideo]);
    return processingVideo.id;
  };

  /**
   * Removes a processing video when it's done
   */
  const removeProcessingVideo = (id: string) => {
    setProcessingVideos(prev => prev.filter(video => video.id !== id));
  };

  /**
   * Callback after successful upload to refresh video list
   */
  const handleUploadSuccess = () => {
    console.log("🎉 Upload successful, refreshing video list");
    // After REST upload, refresh using REST API to get latest data
    refreshVideosREST();
    // Also try GraphQL refetch in case it works
    refetch().catch(err => {
      console.warn("GraphQL refetch failed, using REST data:", err);
    });
  };

  /**
   * Callback when upload starts processing (server received file)
   */
  const handleUploadProcessing = (fileName: string) => {
    console.log("🔄 Video uploaded to server, starting processing:", fileName);
    const processingId = addProcessingVideo(fileName);
    
    // After some time, remove the processing video and refresh the real list
    setTimeout(() => {
      removeProcessingVideo(processingId);
      handleUploadSuccess();
    }, 10000); // 10 seconds - adjust based on typical processing time
    
    return processingId;
  };

  if (error) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon />
          <MuiTypography variant="h6" color="textSecondary">
            Error loading videos
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            {error.message}
          </MuiTypography>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {/* Video Uploader - only show if not readonly */}
      {!readonly && (
        <>
          <VideoUploader
            matchId={matchId}
            videosCount={videos.length + processingVideos.length}
            maxVideos={maxVideos}
            onUploadSuccess={handleUploadSuccess}
            onUploadProcessing={handleUploadProcessing}
          />
          <Divider />
        </>
      )}

      {/* Header with counter */}
      <Header>
        <HeaderTitle>
          <VideoLibraryIcon color="primary" style={{ fontSize: "1.75rem" }} />
          <Typography.H6 style={{ margin: 0 }}>Match Videos</Typography.H6>
        </HeaderTitle>
        <Counter>
          {videos.length + processingVideos.length}/{maxVideos}
        </Counter>
      </Header>

      {/* Loading State */}
      {loading && videos.length === 0 && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {/* Empty State */}
      {!loading && videos.length === 0 && (
        <EmptyState>
          <EmptyIcon />
          <MuiTypography variant="h6" color="textSecondary" gutterBottom>
            No videos yet
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            Upload your first match video above
          </MuiTypography>
        </EmptyState>
      )}

      {/* Videos Grid */}
      {(videos.length > 0 || processingVideos.length > 0) && (
        <VideosGrid container spacing={3}>
          {/* Processing Videos First */}
          {processingVideos.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
              <ProcessingVideoCard video={video} />
            </Grid>
          ))}
          {/* Regular Videos */}
          {videos.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
              <VideoCard 
                video={video} 
                onClick={() => handleVideoClick(video)}
                onDelete={handleVideoDelete}
                showDeleteButton={!readonly}
              />
            </Grid>
          ))}
        </VideosGrid>
      )}

      {/* Video Player Modal */}
      <VideoPlayer
        video={selectedVideo}
        open={isPlayerOpen}
        onClose={handleClosePlayer}
      />
    </Container>
  );
};

export default VideoList;
